import React, { useState, useEffect, useRef } from "react";
import { utcToLocalDateTime, utcToDate } from "../../components/helper";
import { useTranslation } from "../../contexts/TranslationContext";
import "./AllProfiles.scss";
import {
	endOrders,
	getPhotos,
	deletePhoto,
	deleteSelfie,
	sendToTelegram,
	blockUser,
	setPhoto,
	getSelfie,
	deleteUser,
	getAllUsers,
	getUsersDetails,
	recomputeFaceMatch,
} from "../../api/apiServiceFetch";
import ConfirmPopup from "../../components/ConfirmPopup";
import Popup from "../../components/Popup";
import OrderPopup from "../../components/OrderPopup";
import PhotoModal from "../../components/PhotoModal";
import PopupGift from "../../components/PopupGift";
import useAdminRealtimeEvent from "../../hooks/useAdminRealtimeEvent";
import { FILE_BASE_URL } from "../../config/env";
import {
	BLOCK_REASON_OPTIONS,
	CUSTOM_REASON_VALUE,
	getReasonOptionsByActionType,
} from "../../constants/moderationReasons";
import { showActionError } from "../../utils/showActionError";

const ITEMS_PER_PAGE = 30;

const AllProfiles = () => {
	const { t } = useTranslation();
	const realtimeEvent = useAdminRealtimeEvent();

	const [isPopupOpen, setIsPopupOpen] = useState(false);
	const [isOrderPopupOpen, setIsOrderPopupOpen] = useState(false);
	const [isGiftPopupOpen, setIsGiftPopupOpen] = useState(false);
	const [isConfirmPopupOpen, setIsConfirmPopupOpen] = useState(false);
	const [isFilterModalOpen, setIsFilterModalOpen] = useState(false);

	const [users, setUsers] = useState([]);
	const [pageNumber, setPageNumber] = useState(1);
	const [totalPages, setTotalPages] = useState(1);
	const [totalCount, setTotalCount] = useState(0);

	const [expandedUserId, setExpandedUserId] = useState(null);
	const [moreUserId, setMoreUserId] = useState(null);
	const [editUser, setEditUser] = useState(null);
	const [userDetails, setUserDetails] = useState(null);
	const [userPhotos, setUserPhotos] = useState([]);
	const [userSelfie, setUserSelfie] = useState(null);

	const [filterTerm, setFilterTerm] = useState("");
	const [debouncedFilterTerm, setDebouncedFilterTerm] = useState("");

	const [filterCountry, setFilterCountry] = useState("");
	const [filterCity, setFilterCity] = useState("");
	const [filterSex, setFilterSex] = useState(0);

	const [message, setMessage] = useState("");
	const [reason, setReason] = useState("");
	const [showBlock, setShowBlock] = useState(false);
	const [sendBlockReasonToUser, setSendBlockReasonToUser] = useState(false);
	const [selectedBlockReason, setSelectedBlockReason] =
		useState(CUSTOM_REASON_VALUE);
	const [userButtonData, setUserButtonData] = useState(null);
	const [isActionSubmitting, setIsActionSubmitting] = useState(false);
	const [isBlockSubmitting, setIsBlockSubmitting] = useState(false);

	const [loading, setLoading] = useState(true);
	const [loadingPhoto, setLoadingPhoto] = useState(false);
	const [uploadProgress, setUploadProgress] = useState(0);
	const [isVip, setVip] = useState(false);

	const [showModal, setShowModal] = useState(false);
	const [userPhoto, setUserPhoto] = useState(null);
	const [faceMatchBusy, setFaceMatchBusy] = useState(false);

	const filePath = FILE_BASE_URL;

	const handleRecomputeFaceMatch = async (userId) => {
		if (!userId || faceMatchBusy) return;
		setFaceMatchBusy(true);
		try {
			const updated = await recomputeFaceMatch(userId);
			if (expandedRef.current === userId && Array.isArray(updated)) {
				setUserPhotos(updated);
			}
		} catch (err) {
			console.error("recomputeFaceMatch failed", err);
			showActionError(err, "Не удалось пересчитать совпадение");
		} finally {
			setFaceMatchBusy(false);
		}
	};

	const modalRef = useRef(null);
	const fetchIdRef = useRef(0);
	const expandedRef = useRef(null);

	useEffect(() => {
		expandedRef.current = expandedUserId;
	}, [expandedUserId]);

	useEffect(() => {
		const timer = setTimeout(() => {
			setDebouncedFilterTerm(filterTerm.trim());
			setPageNumber(1);
		}, 400);

		return () => clearTimeout(timer);
	}, [filterTerm]);

	const isActionBusy = isActionSubmitting || isBlockSubmitting;

	const fetchUsers = async () => {
		const fetchId = ++fetchIdRef.current;

		try {
			setLoading(true);

			const resp = await getAllUsers(
				pageNumber,
				ITEMS_PER_PAGE,
				debouncedFilterTerm,
				filterCountry,
				filterCity,
				filterSex,
			);

			if (fetchId !== fetchIdRef.current) return;

			const data = resp?.data ?? resp?.Data ?? [];
			const total = resp?.totalCount ?? resp?.TotalCount ?? 0;

			setUsers(Array.isArray(data) ? data : []);
			setTotalCount(total);

			const tp = Math.ceil(total / ITEMS_PER_PAGE);
			setTotalPages(tp < 1 ? 1 : tp);
		} catch (e) {
			if (fetchId !== fetchIdRef.current) return;
			console.error("fetchUsers failed:", e);
			setUsers([]);
			setTotalPages(1);
			setTotalCount(0);
		} finally {
			if (fetchId === fetchIdRef.current) {
				setLoading(false);
			}
		}
	};

	useEffect(() => {
		fetchUsers();
	}, [pageNumber, debouncedFilterTerm, filterCountry, filterCity, filterSex]);

	const resetExpandedData = () => {
		setEditUser(null);
		setUserPhotos([]);
		setUserSelfie(null);
		setUserDetails(null);
		setMoreUserId(null);
		setMessage("");
		setVip(false);
	};

	const fetchUserPhotos = async (userId) => {
		try {
			const resp = await getPhotos(userId);
			if (expandedRef.current !== userId) return;
			setUserPhotos(Array.isArray(resp) ? resp : []);
		} catch {
			if (expandedRef.current !== userId) return;
			setUserPhotos([]);
		}
	};

	const fetchUserSelfie = async (userId) => {
		try {
			const resp = await getSelfie(userId);
			if (expandedRef.current !== userId) return;
			setUserSelfie(resp?.fileName ? resp : null);
		} catch {
			if (expandedRef.current !== userId) return;
			setUserSelfie(null);
		}
	};

	const fetchUserDetails = async (userId) => {
		try {
			const resp = await getUsersDetails(userId);
			if (expandedRef.current !== userId) return;
			setUserDetails(resp ?? null);
		} catch {
			if (expandedRef.current !== userId) return;
			setUserDetails(null);
		}
	};

	const handleExpandClick = (userId) => {
		setExpandedUserId((prev) => {
			if (prev === userId) {
				resetExpandedData();
				return null;
			}

			const user = users.find((u) => u.userId === userId);

			setEditUser(user ?? null);
			setVip([1].includes(user?.type));
			setUserPhotos([]);
			setUserSelfie(null);
			setUserDetails(null);
			setMoreUserId(null);
			setMessage("");

			fetchUserPhotos(userId);
			fetchUserSelfie(userId);
			fetchUserDetails(userId);

			return userId;
		});
	};

	const refreshUser = async (userId) => {
		if (!userId) return;

		try {
			const [photosResp, selfieResp, detailsResp] = await Promise.allSettled([
				getPhotos(userId),
				getSelfie(userId),
				getUsersDetails(userId),
			]);

			const photos =
				photosResp.status === "fulfilled" && Array.isArray(photosResp.value)
					? photosResp.value
					: [];

			const selfie =
				selfieResp.status === "fulfilled" && selfieResp.value?.fileName
					? selfieResp.value
					: null;

			const details =
				detailsResp.status === "fulfilled" ? detailsResp.value : null;

			if (expandedUserId === userId) {
				setUserPhotos(photos);
				setUserSelfie(selfie);
				setUserDetails(details);
			}
		} catch (e) {
			console.error("refreshUser failed:", e);
		}
	};

	const updateTotals = (nextCount) => {
		const safeCount = Math.max(0, nextCount);
		setTotalCount(safeCount);
		setTotalPages(Math.max(1, Math.ceil(safeCount / ITEMS_PER_PAGE)));
	};

	const removeUserFromList = (userId) => {
		const exists = users.some((u) => Number(u.userId) === Number(userId));
		setUsers((prev) => prev.filter((u) => Number(u.userId) !== Number(userId)));

		if (exists) {
			updateTotals(totalCount - 1);
		}

		if (expandedUserId === userId) {
			setExpandedUserId(null);
			resetExpandedData();
		}
	};

	const calcAge = (birthdayValue) => {
		if (!birthdayValue) return 0;

		const date = new Date(birthdayValue);
		if (Number.isNaN(date.getTime())) return 0;

		const now = new Date();
		let age = now.getFullYear() - date.getFullYear();
		const monthDiff = now.getMonth() - date.getMonth();
		if (monthDiff < 0 || (monthDiff === 0 && now.getDate() < date.getDate())) {
			age -= 1;
		}

		return age > 0 ? age : 0;
	};

	const normalize = (value) =>
		String(value ?? "")
			.trim()
			.toLowerCase();

	const buildRealtimeUser = (payload) => {
		const name = payload?.name ?? "";
		const surName = payload?.surName ?? "";
		const fullNameRaw = payload?.fullName ?? `${name} ${surName}`;

		return {
			userId: Number(payload?.userId || 0),
			tgId: Number(payload?.tgId || 0),
			fullName: String(fullNameRaw || "").trim(),
			name,
			sName: surName,
			userName: payload?.userName ?? "",
			tgFullName: payload?.tgFullName ?? "",
			gender: Number(payload?.gender || 0),
			age: calcAge(payload?.birthday),
			birthday: payload?.birthday ?? null,
			city: payload?.city ?? "",
			country: payload?.country ?? "",
			language: payload?.language ?? "ru",
			type: 0,
			otpConfirmed: false,
			isActive: true,
			freezeState: false,
			incoState: false,
			createDate: payload?.createDate ?? new Date().toISOString(),
			lastLoginDate: payload?.createDate ?? new Date().toISOString(),
		};
	};

	const matchesRealtimeFilters = (user) => {
		if (!user?.userId) return false;

		if (Number(filterSex) > 0 && Number(user.gender) !== Number(filterSex)) {
			return false;
		}

		if (filterCountry && normalize(user.country) !== normalize(filterCountry)) {
			return false;
		}

		if (filterCity && normalize(user.city) !== normalize(filterCity)) {
			return false;
		}

		if (filterTerm) {
			const term = normalize(filterTerm);
			const haystack = [
				user.fullName,
				user.userName,
				user.city,
				user.country,
				user.tgId,
				user.userId,
			]
				.map(normalize)
				.join(" ");

			if (!haystack.includes(term)) {
				return false;
			}
		}

		return true;
	};

	useEffect(() => {
		if (!realtimeEvent?.type) return;

		const eventType = String(realtimeEvent.type);
		const payload = realtimeEvent.payload ?? {};
		const eventUserId = Number(payload?.userId || 0);

		if (!eventUserId) return;

		const removeEvents = new Set([
			"user_deleted",
			"user_blocked",
			"user_confirmed",
		]);

		if (removeEvents.has(eventType)) {
			removeUserFromList(eventUserId);
			return;
		}

		if (eventType === "user_registered") {
			if (pageNumber !== 1) return;

			const nextUser = buildRealtimeUser(payload);
			if (!matchesRealtimeFilters(nextUser)) return;
			if (users.some((u) => Number(u.userId) === nextUser.userId)) return;

			setUsers((prev) => [nextUser, ...prev].slice(0, ITEMS_PER_PAGE));
			updateTotals(totalCount + 1);
			return;
		}

		const detailEvents = new Set([
			"photo_added",
			"photo_updated",
			"photo_deleted",
			"photo_approved",
			"selfie_added",
			"selfie_updated",
			"selfie_deleted",
			"selfie_approved",
			"user_updated",
		]);

		if (detailEvents.has(eventType) && expandedUserId === eventUserId) {
			void refreshUser(eventUserId);
		}
	}, [realtimeEvent]); // eslint-disable-line react-hooks/exhaustive-deps

	const handleViewPhoto = (src) => {
		setUserPhoto(src);
		setShowModal(true);
	};

	const getActionReasonOptions = () =>
		getReasonOptionsByActionType(userButtonData?.type);

	const handleBlockReasonChange = (nextReason) => {
		setSelectedBlockReason(nextReason);
		setReason(nextReason === CUSTOM_REASON_VALUE ? "" : nextReason);
	};

	const handleByType = (type, id, userId) => {
		if (isActionBusy) return;
		setUserButtonData({ type, id, userId });
		setIsConfirmPopupOpen(true);
	};

	const handleButton = (type) => {
		if (isActionBusy) return;
		setUserButtonData({ type, id: 0, userId: expandedUserId });

		if (type === "block") {
			setSelectedBlockReason(CUSTOM_REASON_VALUE);
			setReason("");
			setSendBlockReasonToUser(false);
			setShowBlock(true);
			return;
		}

		setIsConfirmPopupOpen(true);
	};

	const handleConfirmPopup = async (
		deleteNoticeMessage = "",
		suppressNotification = true,
	) => {
		if (!userButtonData || isActionBusy) return;

		const { type, id, userId } = userButtonData;

		try {
			setIsActionSubmitting(true);
			if (type === "photoDelete") {
				await deletePhoto(id, {
					message: deleteNoticeMessage,
					suppressNotification,
				});
				await refreshUser(userId);
			}

			if (type === "selfieDelete") {
				await deleteSelfie(id, {
					message: deleteNoticeMessage,
					suppressNotification,
				});
				await refreshUser(userId);
			}

			if (type === "END") {
				await endOrders(userId);
				setVip(false);
			}

			if (type === "userDelete") {
				await deleteUser({
					userId,
					message: deleteNoticeMessage,
					suppressNotification,
				});
				removeUserFromList(userId);
			}

			if (type === "sendMsg") {
				await sendToTelegram({
					chatId: userId,
					messageKind: 3,
					message,
					language: "ru",
				});

				setMessage("");
			}
		} catch (e) {
			console.error("Action failed:", e);
			showActionError(e, "Ошибка выполнения действия");
		} finally {
			setIsActionSubmitting(false);
			setIsConfirmPopupOpen(false);
			setUserButtonData(null);
		}
	};

	const applyBlock = async () => {
		if (isActionBusy || !expandedUserId) return;
		try {
			setIsBlockSubmitting(true);
			await blockUser({
				userId: expandedUserId,
				reason,
				sendReasonToUser: sendBlockReasonToUser,
			});

			removeUserFromList(expandedUserId);
			setShowBlock(false);
			setReason("");
			setSendBlockReasonToUser(false);
			setSelectedBlockReason(CUSTOM_REASON_VALUE);
			setIsBlockSubmitting(false);
		} catch (e) {
			console.error(e);
			showActionError(e, "Не удалось заблокировать");
			setIsBlockSubmitting(false);
		}
	};

	const handleFileUpload = async (e, userId) => {
		const file = e.target.files?.[0];

		if (!file || loadingPhoto || isActionBusy) return;

		setLoadingPhoto(true);
		setUploadProgress(0);

		try {
			const isMain = !userPhotos?.length;

			const response = await setPhoto(file, userId, file.name, isMain, 0, (p) =>
				setUploadProgress(Math.round(p)),
			);

			const newPhoto = {
				id: response?.id || Date.now(),
				fileName: response?.fileName || file.name,
				moderated: true,
				userId,
				createDate: Date.now(),
				isMain,
			};

			setUserPhotos((prev) => [...prev, newPhoto]);
		} catch (err) {
			console.error("Error uploading photo:", err);
			showActionError(err, "Ошибка при загрузке фото");
		} finally {
			setLoadingPhoto(false);
			setUploadProgress(0);

			if (e?.target) {
				e.target.value = "";
			}
		}
	};

	const applyFiltersHandler = () => {
		setPageNumber(1);
		setIsFilterModalOpen(false);
	};

	const handleFilterRemove = () => {
		setFilterTerm("");
		setFilterCountry("");
		setFilterCity("");
		setFilterSex(0);
		setPageNumber(1);
	};

	const handleSearchChange = (e) => {
		setFilterTerm(e.target.value);
	};

	const handlePreviousPage = () => {
		if (pageNumber > 1) {
			setPageNumber((prev) => prev - 1);
		}
	};

	const handleFirstPage = () => {
		setPageNumber(1);
	};
	const handleLastPage = () => {
		setPageNumber(totalPages);
	};
	const handleNextPage = () => {
		if (pageNumber < totalPages) {
			setPageNumber((prev) => prev + 1);
		}
	};

	useEffect(() => {
		const handleClickOutside = (event) => {
			if (modalRef.current && !modalRef.current.contains(event.target)) {
				setIsFilterModalOpen(false);
			}
		};

		if (isFilterModalOpen) {
			document.addEventListener("mousedown", handleClickOutside);
		}

		return () => {
			document.removeEventListener("mousedown", handleClickOutside);
		};
	}, [isFilterModalOpen]);

	const Info = ({ label, value, wide = false, danger = false }) => (
		<div
			className={`info-cell ${wide ? "wide" : ""} ${danger ? "danger" : ""}`}
		>
			<span>{label}</span>
			<strong>{value || "-"}</strong>
		</div>
	);

	const getGenderText = (gender) => (gender === 1 ? "Мужчина" : "Женщина");

	const getPrimaryInfo = (user) => [
		{ label: "Возраст", value: user.age || "-" },
		{ label: "Город", value: user.city || "-" },
		{ label: "TG ID", value: user.tgId || "-" },
		{
			label: "Профессия",
			value: userDetails?.profession ? t(userDetails.profession) : "-",
		},
	];

	const getMoreInfo = (user) => [
		{ label: "Дата рождения", value: utcToDate(user.birthday) },
		{ label: "Страна", value: user.country || "-" },
		{ label: "Язык", value: user.language || "-" },
		{ label: "Telegram имя", value: user.tgFullName || "-" },
		{
			label: "Никнейм",
			value: user.userName ? (
				<a
					href={`https://t.me/${user.userName}`}
					target="_blank"
					rel="noopener noreferrer"
				>
					@{user.userName}
				</a>
			) : (
				"-"
			),
		},
		{ label: "Почта", value: user.email || "-" },
		{ label: "OTP", value: user.otp || "-" },
		{ label: "OTP подтверждён", value: user.otpConfirmed ? "Да" : "Нет" },
		{
			label: "Роль",
			value: userDetails?.wRole ? t(userDetails.wRole) : "-",
		},
		{
			label: "Мировоззрение",
			value: userDetails?.worldview ? t(userDetails.worldview) : "-",
		},
		{
			label: "Цель",
			value: userDetails?.goal ? t(userDetails.goal) : "-",
		},
		{
			label: "Дети",
			value: userDetails?.children ? t(userDetails.children) : "-",
		},
		{
			label: "Возраст партнёра",
			value:
				userDetails?.minAge || userDetails?.maxAge
					? `${userDetails?.minAge ?? "-"} - ${userDetails?.maxAge ?? "-"}`
					: "-",
		},
		{
			label: "Рост",
			value:
				userDetails?.height && userDetails.height > 0
					? userDetails.height
					: "-",
		},
		{
			label: "Регистрация",
			value: utcToLocalDateTime(user.createDate),
		},
		{
			label: "Последний вход",
			value: utcToLocalDateTime(user.lastLoginDate),
		},
		{ label: "Активен", value: user.isActive ? "Да" : "Нет" },
		{ label: "Заморожен", value: user.freezeState ? "Да" : "Нет" },
		{ label: "Инкогнито", value: user.incoState ? "Да" : "Нет" },
	];

	const isInitialLoading = loading && users.length === 0 && totalCount === 0;

	return (
		<div className="AllProfiles compactProfiles">
			{showModal && (
				<PhotoModal photo={userPhoto} onClose={() => setShowModal(false)} />
			)}

			<ConfirmPopup
				isOpen={isConfirmPopupOpen}
				onConfirm={handleConfirmPopup}
				withMessageInput={[
					"photoDelete",
					"selfieDelete",
					"userDelete",
				].includes(userButtonData?.type)}
				withSkipNotificationToggle={[
					"photoDelete",
					"selfieDelete",
					"userDelete",
				].includes(userButtonData?.type)}
				title="Подтвердить действие"
				skipNotificationLabel="Не отправлять уведомление"
				messageLabel="Причина для пользователя (необязательно)"
				messagePlaceholder="Например: доступ к приложению отклонен"
				reasonOptions={getActionReasonOptions()}
				reasonSelectLabel="Готовая причина"
				customReasonLabel="Свой вариант"
				isSubmitting={isActionSubmitting}
				onCancel={() => {
					setIsConfirmPopupOpen(false);
					setUserButtonData(null);
				}}
			/>

			<PopupGift
				isOpen={isGiftPopupOpen}
				onClose={() => setIsGiftPopupOpen(false)}
				setVip={setVip}
				userId={expandedUserId}
				isVip={isVip}
			/>

			<Popup
				isOpen={isPopupOpen}
				onClose={() => setIsPopupOpen(false)}
				editData={editUser}
				userDetails={userDetails}
				onSaved={(savedUser) => {
					setUsers((prev) =>
						prev.map((user) =>
							Number(user.userId) === Number(savedUser.userId)
								? {
										...user,
										fullName:
											savedUser.fullName ||
											[user?.name, user?.surName].filter(Boolean).join(" "),
										city: savedUser.city || user.city,
										country: savedUser.country || user.country,
										gender: Number(savedUser.gender) || user.gender,
										freezeState: Boolean(savedUser.freezeState),
										incoState: Boolean(savedUser.incoState),
									}
								: user,
						),
					);

					setEditUser((prev) =>
						prev && Number(prev.userId) === Number(savedUser.userId)
							? {
									...prev,
									name: savedUser.name,
									surName: savedUser.surName,
									fullName: savedUser.fullName || prev.fullName,
									city: savedUser.city,
									country: savedUser.country,
									gender: savedUser.gender,
									freezeState: savedUser.freezeState,
									incoState: savedUser.incoState,
								}
							: prev,
					);

					setUserDetails((prev) =>
						prev
							? {
									...prev,
									worldview: savedUser.worldview,
									description: savedUser.description,
									profession: savedUser.profession,
									goal: savedUser.goal,
									familyNames: savedUser.familyNames,
									children: savedUser.children,
									minAge: savedUser.minAge,
									maxAge: savedUser.maxAge,
									height: savedUser.height,
									languages: savedUser.languages,
								}
							: prev,
					);

					void fetchUserDetails(savedUser.userId);
				}}
				onConfirm={() => {
					setUsers((prev) =>
						prev.filter((user) => user.userId !== expandedUserId),
					);
					setIsPopupOpen(false);
				}}
			/>

			<OrderPopup
				userId={expandedUserId}
				visible={isOrderPopupOpen}
				onClose={() => setIsOrderPopupOpen(false)}
			/>

			<div className="profiles-toolbar">
				<div className="profiles-search">
					<input
						type="text"
						placeholder="Поиск..."
						value={filterTerm}
						onChange={handleSearchChange}
					/>
				</div>

				<button type="button" onClick={() => setIsFilterModalOpen(true)}>
					Фильтр
				</button>

				<button type="button" className="ghost" onClick={handleFilterRemove}>
					Сброс
				</button>

				<span className="profiles-count">Всего: {totalCount}</span>
			</div>
			{isInitialLoading ? (
				<div className="profiles-loading">Загрузка...</div>
			) : null}

			{loading && !isInitialLoading ? (
				<div className="profiles-inline-loading">Обновление...</div>
			) : null}

			{showBlock && (
				<div className="filter-modal">
					<div className="filter-modal-content compact-modal">
						<h3>Заблокировать пользователя</h3>

						<select
							value={selectedBlockReason}
							onChange={(e) => handleBlockReasonChange(e.target.value)}
							disabled={isBlockSubmitting}
						>
							<option value={CUSTOM_REASON_VALUE}>Свой вариант</option>
							{BLOCK_REASON_OPTIONS.map((option) => (
								<option key={option} value={option}>
									{option}
								</option>
							))}
						</select>

						<textarea
							className="textArea"
							value={reason}
							onChange={(e) => setReason(e.target.value)}
							placeholder="Причина блокировки"
							disabled={isBlockSubmitting}
						/>

						<div className="block-reason-note">
							Этот текст сохраняется для модерации и по умолчанию не
							отправляется пользователю.
						</div>
						<label className="block-reason-check">
							<input
								type="checkbox"
								checked={sendBlockReasonToUser}
								onChange={(e) => setSendBlockReasonToUser(e.target.checked)}
								disabled={isBlockSubmitting}
							/>
							<span>Отправить эту причину пользователю</span>
						</label>

						<div className="modal-row-actions">
							<button
								type="button"
								onClick={applyBlock}
								disabled={isBlockSubmitting}
							>
								Заблокировать
							</button>
							<button
								type="button"
								className="ghost"
								disabled={isBlockSubmitting}
								onClick={() => {
									setShowBlock(false);
									setSendBlockReasonToUser(false);
								}}
							>
								Отмена
							</button>
						</div>
					</div>
				</div>
			)}

			{isFilterModalOpen && (
				<div className="filter-modal">
					<div className="filter-modal-content compact-modal" ref={modalRef}>
						<h3>Фильтр</h3>

						<input
							type="text"
							placeholder="Страна"
							value={filterCountry}
							onChange={(e) => setFilterCountry(e.target.value)}
						/>

						<input
							type="text"
							placeholder="Город"
							value={filterCity}
							onChange={(e) => setFilterCity(e.target.value)}
						/>

						<select
							value={filterSex}
							onChange={(e) => setFilterSex(Number(e.target.value))}
						>
							<option value={0}>Пол</option>
							<option value={1}>Мужчина</option>
							<option value={2}>Женщина</option>
						</select>

						<div className="modal-row-actions">
							<button type="button" onClick={applyFiltersHandler}>
								Применить
							</button>
							<button
								type="button"
								className="ghost"
								onClick={() => setIsFilterModalOpen(false)}
							>
								Отмена
							</button>
						</div>
					</div>
				</div>
			)}

			{!isInitialLoading && users.length === 0 ? (
				<div className="profiles-empty">Нет профилей</div>
			) : null}

			{users.map((user) => {
				const isOpen = expandedUserId === user.userId;
				const isMoreOpen = moreUserId === user.userId;

				return (
					<div
						key={user.userId}
						className={`profile-card ${isOpen ? "open" : ""}`}
					>
						<button
							type="button"
							className={`profile-summary ${user.gender === 1 ? "male" : "female"}`}
							onClick={() => handleExpandClick(user.userId)}
						>
							<div className="summary-main">
								<strong>
									{user.fullName || "Без имени"} <span>#{user.userId}</span>
								</strong>

								<div className="summary-meta">
									<span>
										{user.userName ? (
											<a
												href={`https://t.me/${user.userName}`}
												target="_blank"
												rel="noopener noreferrer"
												onClick={(e) => e.stopPropagation()}
											>
												@{user.userName}
											</a>
										) : (
											"@-"
										)}
									</span>
									<span>{getGenderText(user.gender)}</span>
									<span>{user.age ? `${user.age} лет` : "Возраст -"}</span>
									<span>{user.city || "Город -"}</span>
								</div>
							</div>

							<div className="summary-right">
								{user.type === 1 && <span className="plan-badge">PREMIUM</span>}
								<span className="open-indicator">{isOpen ? "−" : "+"}</span>
							</div>
						</button>

						{isOpen && (
							<div className="profile-details">
								<div className="profile-content-grid">
									<section className="media-panel">
										<div className="media-header">
											<strong>Фото</strong>

											<label className="upload-small">
												+ Добавить
												<input
													type="file"
													accept="image/*"
													disabled={loadingPhoto || isActionBusy}
													onChange={(e) => handleFileUpload(e, user.userId)}
												/>
											</label>
										</div>

										{loadingPhoto && (
											<div className="upload-progress">
												Загрузка: {uploadProgress}%
											</div>
										)}

										{(userSelfie?.fileName || userPhotos.length > 0) && (
											<div className="face-match-bar">
												<button
													type="button"
													className="face-match-button"
													disabled={
														faceMatchBusy ||
														!userSelfie?.fileName ||
														userPhotos.length === 0
													}
													onClick={() => handleRecomputeFaceMatch(user.userId)}
													title={
														!userSelfie?.fileName
															? "Сначала загрузите селфи"
															: "Сверить лицо селфи с фото"
													}
												>
													{faceMatchBusy ? "Считаем..." : "Сверить лицо"}
												</button>
											</div>
										)}

										<div className="media-grid">
											{userSelfie?.fileName ? (
												<div className="media-item selfie">
													<img
														src={`${filePath}/Selfie/${user.userId}/${userSelfie.fileName}`}
														onClick={() =>
															handleViewPhoto(
																`${filePath}/Selfie/${user.userId}/${userSelfie.fileName}`,
															)
														}
														alt="Селфи"
													/>

													<button
														type="button"
														disabled={isActionBusy}
														onClick={() =>
															handleByType(
																"selfieDelete",
																userSelfie.id,
																user.userId,
															)
														}
													>
														×
													</button>
												</div>
											) : (
												<div className="media-empty">Нет селфи</div>
											)}

											{userPhotos.length > 0 ? (
												userPhotos.map((photo, index) => {
													const matchTier =
														typeof photo.matchPercent === "number"
															? photo.matchPercent >= 50
																? "high"
																: photo.matchPercent >= 30
																	? "mid"
																	: "low"
															: "none";
													return (
														<div key={photo.id} className="media-item">
														<img
															src={`${filePath}/Photo/${user.userId}/${photo.fileName}`}
															onClick={() =>
																handleViewPhoto(
																	`${filePath}/Photo/${user.userId}/${photo.fileName}`,
																)
															}
															alt={`Фото ${index + 1}`}
														/>

														<div
															className={`face-match-badge face-match-${matchTier}`}
															title={
																matchTier === "none"
																	? "Лицо не распознано или не сверялось"
																	: `Совпадение с селфи: ${photo.matchPercent.toFixed(0)}%`
															}
														>
															{matchTier === "none"
																? "—"
																: `${photo.matchPercent.toFixed(0)}%`}
														</div>

														<div className="media-actions">
															<button
																type="button"
																disabled={isActionBusy}
																onClick={() =>
																	handleByType(
																		"photoDelete",
																		photo.id,
																		user.userId,
																	)
																}
															>
																×
															</button>
														</div>
													</div>
													);
												})
											) : (
												<div className="media-empty">Нет фото</div>
											)}
										</div>
									</section>
									<section className="profile-info-panel">
										<div className="primary-info-grid">
											{getPrimaryInfo(user).map((item) => (
												<Info
													key={item.label}
													label={item.label}
													value={item.value}
												/>
											))}

											<Info
												label="О себе"
												value={userDetails?.description || "-"}
												wide
											/>

											<button
												type="button"
												className="more-button"
												onClick={() =>
													setMoreUserId((prev) =>
														prev === user.userId ? null : user.userId,
													)
												}
											>
												{isMoreOpen ? "Скрыть" : "Подробнее"}
											</button>
										</div>

										{isMoreOpen && (
											<div className="more-info-grid">
												{getMoreInfo(user).map((item) => (
													<Info
														key={item.label}
														label={item.label}
														value={item.value}
													/>
												))}

												<Info
													label="Фамилии"
													value={userDetails?.familyNames || "-"}
													wide
												/>

												{(userDetails?.cntReg > 1 ||
													userDetails?.cntDel > 0) && (
													<Info
														label="История"
														wide
														danger
														value={`${userDetails?.cntReg > 1 ? `Регистраций: ${userDetails.cntReg}` : ""} ${
															userDetails?.cntDel > 0
																? `${user.gender === 2 ? "Удалилась" : "Удалился"}: ${userDetails.cntDel}`
																: ""
														}`}
													/>
												)}
											</div>
										)}

										<div className="profile-actions-compact">
											<button
												type="button"
												disabled={isActionBusy}
												onClick={() => setIsPopupOpen(true)}
											>
												Редактировать
											</button>

											<button
												type="button"
												className="purple"
												disabled={isActionBusy}
												onClick={() => setIsOrderPopupOpen(true)}
											>
												Покупки
											</button>

											<button
												type="button"
												className="danger"
												disabled={isActionBusy}
												onClick={() => handleButton("block")}
											>
												Блок
											</button>

											<button
												type="button"
												className="danger"
												disabled={isActionBusy}
												onClick={() => handleButton("userDelete")}
											>
												Удалить
											</button>

											<button
												type="button"
												disabled={!isVip || isActionBusy}
												onClick={() => {
													setUserButtonData({
														type: "END",
														id: 0,
														userId: expandedUserId,
													});
													if (!isActionBusy) {
														setIsConfirmPopupOpen(true);
													}
												}}
											>
												Убрать подписку
											</button>

											<button
												type="button"
												disabled={isActionBusy}
												onClick={() => setIsGiftPopupOpen(true)}
											>
												{isVip ? "Продлить Premium" : "Подарить Premium"}
											</button>
										</div>

										<div className="message-compact">
											<textarea
												value={message}
												onChange={(e) => setMessage(e.target.value)}
												placeholder="Сообщение пользователю"
												disabled={isActionBusy}
											/>

											<button
												type="button"
												onClick={() => handleButton("sendMsg")}
												disabled={!message.trim() || isActionBusy}
											>
												Отправить
											</button>
										</div>
									</section>
								</div>
							</div>
						)}
					</div>
				);
			})}

			<div className="pagination">
				<button onClick={handleFirstPage} disabled={pageNumber === 1}>
					На первую
				</button>
				<button onClick={handlePreviousPage} disabled={pageNumber === 1}>
					Назад
				</button>

				<span>
					{pageNumber} / {totalPages}
				</span>

				<button onClick={handleNextPage} disabled={pageNumber === totalPages}>
					Дальше
				</button>

				<button onClick={handleLastPage} disabled={pageNumber === totalPages}>
					На последнюю
				</button>
			</div>
		</div>
	);
};

export default AllProfiles;
